import React from 'react';

interface SegmentedToggleOption<T extends string> {
  key: T;
  label: React.ReactNode;
}

interface SegmentedToggleProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: Array<SegmentedToggleOption<T>>;
  size?: 'sm' | 'md';
  ariaLabel?: string;
  className?: string;
  /** Stretch segments equally on viewports below sm. */
  fullWidthOnMobile?: boolean;
}

function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  ariaLabel,
  className = '',
  fullWidthOnMobile = false,
}: SegmentedToggleProps<T>) {
  const activeIndex = Math.max(0, options.findIndex(o => o.key === value));

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const buttonRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const [thumbStyle, setThumbStyle] = React.useState<{ left: number; width: number }>({ left: 0, width: 0 });
  // Use state (not ref) to control animation - this ensures React re-renders with the correct value
  const [enableTransition, setEnableTransition] = React.useState(false);

  // Re-measure when labels change (i18n) — length alone is not enough
  const optionsSignature = options
    .map((o) => `${o.key}:${typeof o.label === 'string' ? o.label : ''}`)
    .join('|');

  const measure = React.useCallback(() => {
    const btn = buttonRefs.current[activeIndex];
    const container = containerRef.current;
    if (!btn || !container) return;
    const left = btn.offsetLeft;
    const width = btn.offsetWidth;
    setThumbStyle((prev) =>
      prev.left === left && prev.width === width ? prev : { left, width }
    );
  }, [activeIndex]);

  // Measure before paint — prevents jump when value / labels / size change
  React.useLayoutEffect(() => {
    measure();
  }, [measure, value, size, optionsSignature]);

  // Enable transitions only after the first paint
  React.useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEnableTransition(true);
      });
    });
  }, []);

  // ResizeObserver: language switch, font load, zoom, container resize
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      const onResize = () => measure();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(container);
    buttonRefs.current.forEach((btn) => {
      if (btn) observer.observe(btn);
    });

    return () => observer.disconnect();
  }, [measure, optionsSignature]);

  const containerBase = `relative flex items-center rounded-full bg-gray-100/90 p-1 ${
    fullWidthOnMobile ? 'w-full sm:inline-flex sm:w-auto' : 'inline-flex'
  }`;
  const sizeContainer = size === 'sm' ? 'text-sm' : 'text-sm';
  const buttonBase =
    'relative z-10 inline-flex items-center justify-center font-medium rounded-full whitespace-nowrap transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-0';
  const sizeHeights = size === 'sm' ? 'h-7' : 'h-9';
  const sizePadding = size === 'sm' ? 'px-3' : 'px-4';
  const mobileStretch = fullWidthOnMobile ? 'min-w-0 flex-1 sm:flex-initial' : '';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (activeIndex + delta + options.length) % options.length;
    onChange(options[nextIndex].key);
    requestAnimationFrame(() => {
      buttonRefs.current[nextIndex]?.focus();
    });
  };

  return (
    <div
      className={`${containerBase} ${sizeContainer} ${className} outline-none`}
      role="radiogroup"
      aria-label={ariaLabel}
      ref={containerRef}
    >
      <div
        aria-hidden
        className="absolute top-1 bottom-1 rounded-full bg-primary-600 shadow-sm shadow-primary-600/25"
        style={{
          width: `${thumbStyle.width}px`,
          left: `${thumbStyle.left}px`,
          // Only animate after first paint to avoid visual jump on initial render
          transition: enableTransition
            ? 'left 200ms cubic-bezier(0.4, 0, 0.2, 1), width 200ms cubic-bezier(0.4, 0, 0.2, 1)'
            : 'none',
        }}
      />

      {options.map((opt, idx) => {
        const isActive = value === opt.key;
        const colorClasses = isActive
          ? 'text-white'
          : 'text-gray-500 hover:text-gray-800';
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={isActive}
            tabIndex={isActive ? 0 : -1}
            onMouseDown={(e) => e.preventDefault()}
            onKeyDown={handleKeyDown}
            onClick={() => onChange(opt.key)}
            className={`${buttonBase} ${sizeHeights} ${sizePadding} ${mobileStretch} ${colorClasses}`}
            ref={(el) => {
              buttonRefs.current[idx] = el;
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedToggle;
